interface User {
  id: string;
  email: string;
  name: string;
  perfil?: string;
  tenantId?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
  tenantId?: string;
}

interface LoginResponse {
  token: string;
  tipo: string;
  usuarioId: string;
  nome: string;
  email: string;
  perfil: 'ADMIN' | 'GERENTE' | 'OPERADOR' | 'VENDEDOR' | 'ESTOQUISTA' | 'FINANCEIRO' | 'COMPRADOR';
  tenantId: string;
  expiresAt: string;
}

class AuthService {
  private user: User | null = null;
  private token: string | null = null;
  private loginData: LoginResponse | null = null;
  private companyId: string | null = null;
  private companyName: string | null = null;

  constructor() {
    // Recuperar dados do localStorage na inicialização
    this.token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const loginDataStr = localStorage.getItem('loginData');
    this.companyId = localStorage.getItem('companyId');
    this.companyName = localStorage.getItem('companyName');
    
    if (userData) {
      this.user = JSON.parse(userData);
    }
    if (loginDataStr) {
      this.loginData = JSON.parse(loginDataStr);
    }
  }

  init(): void {
    // Método para inicialização se necessário
  }

  async login(credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> {
    try {
      // Montar dinamicamente a URL do login para funcionar via localhost e IP
      const { resolveBaseURL } = await import('./apiBase');
      const base = resolveBaseURL();
      const endpoint = `${base}v1/auth/login`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };
      if (credentials.tenantId) {
        headers['X-Tenant-ID'] = credentials.tenantId;
      }
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          username: credentials.email,
          email: credentials.email,
          senha: credentials.password,
          tenantId: credentials.tenantId
        }),
      });

      if (response.ok) {
        const data: LoginResponse = await response.json();
        
        // Armazenar todos os dados retornados pelo login
        this.loginData = data;
        this.token = data.token;
        this.user = {
          id: data.usuarioId,
          email: data.email,
          name: data.nome,
          perfil: data.perfil,
          tenantId: data.tenantId
        };
         
        // Salvar todos os dados no localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(this.user));
        localStorage.setItem('loginData', JSON.stringify(data));
        localStorage.setItem('userProfile', data.perfil);
        localStorage.setItem('tenantId', data.tenantId);
        localStorage.setItem('tokenType', data.tipo);
        localStorage.setItem('tokenExpiresAt', data.expiresAt);
        
        return { success: true };
      } else {
        // Tratar erros específicos da API
        const errorData = await response.json().catch(() => null);
        let errorMessage = 'Erro ao fazer login';
        
        switch (response.status) {
          case 400:
            errorMessage = errorData?.message || 'Dados inválidos. Verifique email, senha e tenant ID.';
            break;
          case 401:
            errorMessage = 'Email ou senha incorretos.';
            break;
          case 403:
            errorMessage = 'Acesso negado. Verifique suas permissões.';
            break;
          case 404:
            errorMessage = 'Serviço de autenticação não encontrado.';
            break;
          case 500:
            errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
            break;
          default:
            errorMessage = errorData?.message || `Erro ${response.status}: ${response.statusText}`;
        }
        
        return { success: false, error: errorMessage };
      }
      
      // Fallback: modo demo desativado por padrão
      return { success: false, error: 'Servidor de autenticação indisponível' };
      
    } catch (error) {
      return { success: false, error: 'Erro ao conectar com o servidor' };
    }
  }

  logout(): void {
    this.user = null;
    this.token = null;
    this.loginData = null;
    this.companyId = null;
    this.companyName = null;
    
    // Limpar todos os dados do localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loginData');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyName');
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  getUser(): User | null {
    return this.user;
  }

  getToken(): string | null {
    return this.token;
  }

  getLoginData(): LoginResponse | null {
    return this.loginData;
  }

  getUserProfile(): string | null {
    return this.loginData?.perfil || localStorage.getItem('userProfile');
  }

  getTenantId(): string | null {
    return this.loginData?.tenantId || localStorage.getItem('tenantId');
  }

  getTokenType(): string | null {
    return this.loginData?.tipo || localStorage.getItem('tokenType');
  }

  getTokenExpiresAt(): string | null {
    return this.loginData?.expiresAt || localStorage.getItem('tokenExpiresAt');
  }

  isTokenExpired(): boolean {
    const expiresAt = this.getTokenExpiresAt();
    if (!expiresAt) return false;
    
    const expirationDate = new Date(expiresAt);
    return new Date() > expirationDate;
  }

  // Empresa selecionada
  setCompany(id: string, name?: string): void {
    this.companyId = id;
    this.companyName = name || null;
    localStorage.setItem('companyId', id);
    if (name) localStorage.setItem('companyName', name);
  }

  getCompanyId(): string | null {
    return this.companyId || localStorage.getItem('companyId');
  }

  getCompanyName(): string | null {
    return this.companyName || localStorage.getItem('companyName');
  }

  clearCompany(): void {
    this.companyId = null;
    this.companyName = null;
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyName');
  }

  async requestPasswordReset(email: string, tenantId?: string): Promise<{ success: boolean; message?: string; error?: string }>{
    try {
      const { resolveBaseURL } = await import('./apiBase');
      const base = resolveBaseURL();
      // Candidatos comuns de endpoint de recuperação
      const candidates = [
        'v1/auth/forgot-password',
        'v1/auth/password/forgot',
        'v1/auth/reset-password/request',
        'v1/auth/reset/request'
      ];

      const payload: Record<string, string> = { email };
      if (tenantId) payload.tenantId = tenantId;

      for (const path of candidates) {
        const endpoint = `${base}${path}`;
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const data = await res.json().catch(() => null);
          const msg = data?.message || 'Se existir uma conta com este email, enviamos instruções de redefinição.';
          return { success: true, message: msg };
        }
        // Se 404 ou 405, tenta próximo candidato
        if (res.status !== 404 && res.status !== 405) {
          const errData = await res.json().catch(() => null);
          return { success: false, error: errData?.message || `Erro ${res.status}: ${res.statusText}` };
        }
      }

      // Se nenhum candidato funcionou
      return { success: false, error: 'Endpoint de recuperação de senha não disponível no backend atual.' };
    } catch (e: any) {
      return { success: false, error: e?.message || 'Falha ao conectar ao servidor' };
    }
  }
}

export const authService = new AuthService();
export type { User, LoginCredentials };
